import logoImg from '../../assets/spotless-logo-white.png';

export const PAYMENT_COMPANY = {
  name: 'Spotless AI Robotics Pvt Ltd',
  logoSrc: logoImg,
  address:
    'No.19, Ground floor, Lake square view, Nanjappa Nagar, Singanallur, Coimbatore - 641005',
  email: 'spotlessai.roboticsindia@gmail.com',
  phone: '+91 7092022700',
  gstin: '33ABKCS3255P1ZT',
  bankName: 'IDFC FIRST BANK',
  accountNumber: '10132183772',
  branchIfsc: 'Salem, Gugai branch & IDFB0080595',
  swiftCode: 'IDFBINBBMUM',
  adCode: '2010152',
  ieCode: 'ABKCS3255P',
};

function pad(n) {
  return String(n).padStart(2, '0');
}

export function getFinancialYear(date = new Date()) {
  const month = date.getMonth();
  const year = date.getFullYear();
  const startYear = month >= 3 ? year : year - 1;
  return { startYear, endYear: startYear + 1 };
}

export function generatePaymentInvoiceNumber(seq = 1) {
  const { startYear, endYear } = getFinancialYear();
  return `${pad(seq)} (${startYear}-${endYear})`;
}

export function formatPaymentInvoiceDate(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatGbp(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

export function createEmptyPaymentLineItem() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    description: '',
    durationDays: '',
    ratePerDay: '',
  };
}

export function paymentLineAmount(item) {
  const days = Number(item.durationDays);
  const rate = Number(item.ratePerDay);
  if (Number.isNaN(days) || Number.isNaN(rate)) return 0;
  return days * rate;
}

export function computePaymentInvoiceTotals(model) {
  const subtotal = (model.lineItems || []).reduce((sum, item) => sum + paymentLineAmount(item), 0);
  const total = subtotal;
  const exchangeRate = Number(model.exchangeRate) || 0;
  const inrAmount = exchangeRate > 0 ? Math.round(total * exchangeRate) : 0;
  return { subtotal, total, exchangeRate, inrAmount };
}

export function billToFromUser(user) {
  if (!user) return { billToName: '', billToAddress: '' };
  const bp = user.billingProfile || {};
  const name = bp.legalName || user.organizationName || user.name || '';
  let address = bp.address;
  if (!address && Array.isArray(user.locations) && user.locations[0]?.address) {
    const a = user.locations[0].address;
    address = [a.street, a.town, a.city, a.state, a.pinCode].filter(Boolean).join(', ');
  }
  return { billToName: name, billToAddress: address || '' };
}

const BELOW_TWENTY = [
  'Zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];

const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function wordsUnderThousand(n) {
  if (n === 0) return '';
  if (n < 20) return BELOW_TWENTY[n];
  if (n < 100) {
    const tens = Math.floor(n / 10);
    const rest = n % 10;
    return rest ? `${TENS[tens]} ${BELOW_TWENTY[rest]}` : TENS[tens];
  }
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const head = `${BELOW_TWENTY[hundreds]} Hundred`;
  return rest ? `${head} ${wordsUnderThousand(rest)}` : head;
}

function wordsEn(n) {
  if (n === 0) return 'Zero';
  const billion = Math.floor(n / 1_000_000_000);
  const million = Math.floor((n % 1_000_000_000) / 1_000_000);
  const thousand = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const parts = [];
  if (billion) parts.push(`${wordsUnderThousand(billion)} Billion`);
  if (million) parts.push(`${wordsUnderThousand(million)} Million`);
  if (thousand) parts.push(`${wordsUnderThousand(thousand)} Thousand`);
  if (rest) parts.push(wordsUnderThousand(rest));
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

function wordsIndian(n) {
  if (n === 0) return 'Zero';
  const crore = Math.floor(n / 10_000_000);
  const lakh = Math.floor((n % 10_000_000) / 100_000);
  const thousand = Math.floor((n % 100_000) / 1000);
  const rest = n % 1000;
  const parts = [];
  if (crore) parts.push(`${wordsUnderThousand(crore)} Crore`);
  if (lakh) parts.push(`${wordsUnderThousand(lakh)} Lakh`);
  if (thousand) parts.push(`${wordsUnderThousand(thousand)} Thousand`);
  if (rest) parts.push(wordsUnderThousand(rest));
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

export function amountInWordsGbp(amount) {
  const value = Math.round(Number(amount) || 0);
  if (value <= 0) return '—';
  return `${wordsEn(value)} British Pound${value === 1 ? '' : 's'} (GBP)`;
}

export function amountInWordsInr(amount) {
  const value = Math.round(Number(amount) || 0);
  if (value <= 0) return '—';
  return `Rupees ${wordsIndian(value)} Only`;
}

export function createDefaultPaymentInvoiceModel() {
  const today = new Date();
  return {
    invoiceNumber: generatePaymentInvoiceNumber(1),
    issueDate: today.toISOString().slice(0, 10),
    billToName: '',
    billToAddress: '',
    selectedUserId: '',
    exchangeRate: 126.647,
    lineItems: [
      {
        ...createEmptyPaymentLineItem(),
        description: 'Design Service charges for April and May -2026',
        durationDays: 40,
        ratePerDay: 100,
      },
    ],
    authorizedSignatory: 'Authorized Signatory',
    companyName: PAYMENT_COMPANY.name,
    companyAddress: PAYMENT_COMPANY.address,
    companyEmail: PAYMENT_COMPANY.email,
    companyPhone: PAYMENT_COMPANY.phone,
    companyGstin: PAYMENT_COMPANY.gstin,
    bankName: PAYMENT_COMPANY.bankName,
    accountNumber: PAYMENT_COMPANY.accountNumber,
    branchIfsc: PAYMENT_COMPANY.branchIfsc,
    swiftCode: PAYMENT_COMPANY.swiftCode,
    adCode: PAYMENT_COMPANY.adCode,
    ieCode: PAYMENT_COMPANY.ieCode,
  };
}
